import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  viewChildren,
} from '@angular/core';

export interface ScreeningTabDef {
  id: string;
  label: string;
  /** Optional count pill. */
  count?: number;
  /** Noun read out after the count, e.g. "flags". */
  countNoun?: string;
  /** Renders the count in the danger tone. */
  alert?: boolean;
}

/**
 * Shared pill tab bar for screening surfaces — same chrome as Manage Brief tabs.
 *
 * A real tablist with a roving tabindex and arrow-key navigation. Anchors without an `href`
 * are neither focusable nor announced as controls, so these stay as buttons.
 */
@Component({
  selector: 'scr-screening-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './screening-tabs.html',
})
export class ScreeningTabs {
  readonly tabs = input.required<readonly ScreeningTabDef[]>();
  readonly activeId = input.required<string>();
  readonly ariaLabel = input.required<string>();

  readonly tabSelect = output<string>();

  private readonly tabButtons = viewChildren<ElementRef<HTMLButtonElement>>('tabButton');

  select(id: string): void {
    if (id !== this.activeId()) {
      this.tabSelect.emit(id);
    }
  }

  /** Arrow keys move and activate; Home/End jump to the ends. */
  onKeydown(event: KeyboardEvent): void {
    const tabs = this.tabs();
    const current = tabs.findIndex((tab) => tab.id === this.activeId());
    if (current < 0) {
      return;
    }

    let next = current;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(tabs[next].id);
    this.tabButtons()[next]?.nativeElement.focus();
  }

  /** Composed so a screen reader hears "Content, 12 flags" rather than "Content 12". */
  accessibleLabel(tab: ScreeningTabDef): string {
    if (tab.count === undefined) {
      return tab.label;
    }
    return `${tab.label}, ${tab.count} ${tab.countNoun ?? 'items'}`;
  }

  tabClass(tab: ScreeningTabDef): string {
    return tab.id === this.activeId()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  countClass(tab: ScreeningTabDef): string {
    const selected = tab.id === this.activeId();
    if (tab.alert) {
      return selected
        ? 'bg-red-600 text-white'
        : 'bg-red-50 text-red-700 dark:bg-red-400/10 dark:text-red-400';
    }
    return selected
      ? 'bg-gray-300 text-gray-900 dark:bg-white/20 dark:text-gray-200'
      : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400';
  }
}
